/**
 * @file 長い文を書く欄 (textarea) を、React に値を持たせずに扱う hook である。
 *
 * @what: いまの本文を ref で持ち、欄が出るたびにそこから入れ直す。`attach` を `ref` に渡し、
 *   `value` も `defaultValue` も渡さない。打鍵以外での差し替え (画像の参照の差し込みなど) は
 *   `put` を呼ぶと、手元と欄の両方に入る。
 * @why: React は制御された textarea を更新するたびに `defaultValue` へ書き戻し、それが要素の
 *   子テキストノードの入れ替えになる。iPad の Chrome は入力中の欄の子ノードが入れ替わるたびに
 *   欄の種類を判定し直してパスワードの候補を出し直すので、1文字ごとに入力が止まる (代表の
 *   端末で再現、2026-09-12)。`input` は書き戻しが `value` 属性の変更にしかならないため、
 *   同じ症状は出ない。Safari とシミュレータでは再現しないので、Chromium だけを見ても
 *   気づけない。
 * @failure: 状態を持たないので失敗しない。欄がまだ無いときの `put` は手元だけを直し、次に
 *   欄が出たときに入る。
 */

import { useCallback, useRef } from "react";

export interface UncontrolledText {
  /** いまの本文である。保存や送信のときはここから取る。 */
  textRef: React.MutableRefObject<string>;
  /** 欄の `ref` に渡す。出るたびに手元の本文を入れ直す。 */
  attach: (node: HTMLTextAreaElement | null) => void;
  /** 打鍵以外で本文を差し替える。手元と欄の両方に入る。 */
  put: (next: string) => void;
}

export function useUncontrolledText(initial = ""): UncontrolledText {
  const textRef = useRef(initial);
  const nodeRef = useRef<HTMLTextAreaElement | null>(null);

  const attach = useCallback((node: HTMLTextAreaElement | null) => {
    nodeRef.current = node;
    if (node !== null) node.value = textRef.current;
  }, []);

  const put = useCallback((next: string) => {
    textRef.current = next;
    const node = nodeRef.current;
    if (node !== null && node.value !== next) node.value = next;
  }, []);

  return { textRef, attach, put };
}
